import { FadeIn, FadeInStagger } from "./FadeIn";

/**
 * Airon-aligned feature section.
 *  - subtitle / title / subheading typography matches the design-system (Space Grotesk display, Inter body, mono accent).
 *  - Accent color driven by CSS variables (--airon, --airon-3) so every page inherits the same palette.
 *  - Image panel echoes the hero visual treatment (rounded, soft tint, blue wash).
 */
export default function FeatureSection(props) {
  const {
    image,
    imagePlacement,
    subtitle,
    title,
    subheading,
    features,
    content,
  } = props;

  const imageOrder =
    imagePlacement === "left"
      ? { text: "lg:col-start-2 lg:row-start-1", image: "lg:col-start-1 lg:row-start-1" }
      : { text: "lg:col-start-1 lg:row-start-1", image: "lg:col-start-2 lg:row-start-1" };

  return (
    <FadeInStagger className="sec" style={{ paddingTop: 96, paddingBottom: 96, borderTopColor: "var(--line)" }}>
      <div className="wrap">
        <div className="grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2 lg:items-center">
          <div className={imageOrder.text}>
            <FadeIn>
              <div
                className="sec-tag"
                style={{
                  color: "var(--airon-3)",
                  marginBottom: 16,
                }}
              >
                {subtitle}
              </div>
            </FadeIn>
            <FadeIn>
              <h2
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 500,
                  fontSize: "clamp(30px, 3.8vw, 48px)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.025em",
                  color: "var(--ink)",
                  margin: 0,
                }}
              >
                {title}
              </h2>
            </FadeIn>
            {subheading ? (
              <FadeIn>
                <p
                  style={{
                    marginTop: 20,
                    fontSize: 17,
                    lineHeight: 1.6,
                    color: "var(--ink-2)",
                    maxWidth: "56ch",
                  }}
                >
                  {subheading}
                </p>
              </FadeIn>
            ) : null}

            {features && features.length > 0 ? (
              <dl
                style={{
                  marginTop: 36,
                  display: "grid",
                  gap: 20,
                }}
              >
                {features.map((feature) => (
                  <FadeIn
                    key={feature.title}
                    className="relative"
                    style={{ paddingLeft: 44 }}
                  >
                    <dt
                      style={{
                        position: "relative",
                        fontFamily: "var(--display)",
                        fontWeight: 500,
                        fontSize: 17,
                        letterSpacing: "-0.01em",
                        color: "var(--ink)",
                      }}
                    >
                      {feature?.icon && (
                        <span
                          aria-hidden="true"
                          style={{
                            position: "absolute",
                            left: -44,
                            top: -4,
                            width: 32,
                            height: 32,
                            borderRadius: 10,
                            background: "var(--bg-3)",
                            color: "var(--airon-3)",
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          <feature.icon className="h-4 w-4" />
                        </span>
                      )}
                      {feature.title}
                    </dt>
                    <dd style={{ marginTop: 4, color: "var(--ink-2)", fontSize: 15, lineHeight: 1.55 }}>
                      {feature.description}
                    </dd>
                  </FadeIn>
                ))}
              </dl>
            ) : null}

            {content ? (
              <FadeIn style={{ marginTop: 32 }}>{content}</FadeIn>
            ) : null}
          </div>

          {image ? (
            <FadeIn className={imageOrder.image}>
              <div
                style={{
                  position: "relative",
                  borderRadius: 24,
                  overflow: "hidden",
                  border: "1px solid var(--line)",
                  background:
                    "repeating-linear-gradient(135deg, rgba(57,182,234,0.08) 0 12px, rgba(57,182,234,0.02) 12px 24px), linear-gradient(160deg, var(--bg-3), var(--bg-2))",
                  boxShadow: "0 20px 50px rgba(14,34,51,0.08)",
                }}
              >
                <img
                  src={image}
                  alt={title || "Illustrasjon"}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                    objectFit: "cover",
                    aspectRatio: "4 / 3",
                  }}
                />
              </div>
            </FadeIn>
          ) : null}
        </div>
      </div>
    </FadeInStagger>
  );
}
